import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  applyAllFilters,
  removeFilterFromActiveFilters,
} from 'src/pages/Contacts/CustomHooks/helpers';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';

export interface IndividualFilter {
  Id?: string;
  name?: string;
}

export type IContactAdvancedFilters = {
  roles?: string[];
  property?: IndividualFilter[];
  status?: string[];
  email?: string;
  phone?: string;
  dateRange?: {
    startDate: Date | null;
    endDate: Date | null;
  };
  name?: string;
  selectedUnit?: IndividualFilter[];
  selectedBuilding?: IndividualFilter[];
  selectedLevel?: IndividualFilter[];
};

export interface IUseContactAdvancedFiltersReturn {
  contacts: IContact[];
  handleApplyFilters: (filters: IContactAdvancedFilters) => void;
  resetFilters: () => void;
  activeFilters: IContactAdvancedFilters;
  removeSingleActiveFilter: (filterText: any) => void;
  hasActiveFilters: boolean;
  uniqueContactStatuses: string[];
}

/* Pure advanced-filter hook. Takes a source list of contacts and exposes a
 * filtered view + the active-filter state machine driven by the advanced filter
 * modal. Basic search lives in useContactBasicFilters; data fetching lives in
 * useContactAPI; this hook is intentionally I/O-free. */
export const useContactAdvancedFilters = (
  sourceContacts: IContact[]
): IUseContactAdvancedFiltersReturn => {
  const [activeFilters, setActiveFilters] = useState<IContactAdvancedFilters>(
    {}
  );

  const hasActiveFilters: boolean = useMemo(() => {
    return (
      Object.keys(activeFilters).length > 0 &&
      !Object.values(activeFilters).every((val: any) =>
        Array.isArray(val) ? val.length === 0 : !val
      )
    );
  }, [activeFilters]);

  const contacts: IContact[] = useMemo(() => {
    if (!hasActiveFilters) {
      return sourceContacts;
    }

    return applyAllFilters(sourceContacts, activeFilters);
  }, [hasActiveFilters, sourceContacts, activeFilters]);

  const handleApplyFilters = (filters: IContactAdvancedFilters) => {
    setActiveFilters(filters);
  };

  const resetFilters = () => {
    setActiveFilters({});
  };

  const removeSingleActiveFilter = (filterToRemove: string) => {
    if (!filterToRemove || filterToRemove.trim() === '') {
      toast.error('No filter value provided to remove', {
        autoClose: 10000,
        position: 'top-center',
      });

      return console.error('No filter value provided to remove');
    }

    const updatedActiveFilters: IContactAdvancedFilters =
      removeFilterFromActiveFilters(activeFilters, filterToRemove);

    setActiveFilters(updatedActiveFilters);
  };

  const uniqueContactStatuses: string[] = useMemo(() => {
    return contacts.reduce((accumulator: string[], currentItem: IContact) => {
      const status: string = currentItem.status;

      if (!accumulator.includes(status)) {
        accumulator.push(status);
      }

      return accumulator;
    }, []);
  }, [contacts]);

  return {
    contacts,
    handleApplyFilters,
    resetFilters,
    activeFilters,
    removeSingleActiveFilter,
    hasActiveFilters,
    uniqueContactStatuses,
  };
};
